// biome-ignore-all lint/correctness/useExhaustiveDependencies: TODO - address later
// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useTypeWire } from "@typewirets/react";
import {
  AlertCircle,
  Calculator,
  Calendar,
  CheckCircle,
  DollarSign,
  Hash,
  Loader2,
  Percent,
  Type,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
// Tooltip components no longer used after switching to InfoTooltip
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import type {
  FieldDefinition,
  MetricDefinition,
} from "@/hooks/api/use-field-definitions";
import { ASTNodeType, type LiteralNode } from "@/lib/formula-parser";
import {
  ASTSerializerWire,
  FormulaParserWire,
} from "@/lib/formula-parser.wire";
import type {
  FormulaContext,
  FunctionSignature,
} from "@/lib/formula-validation-service";
import { cn } from "@/lib/utils";
import type {
  ColumnReference,
  FormulaColumnDefinition,
} from "@/types/formula-column";
import {
  FormulaAutocomplete,
  FUNCTION_DESCRIPTIONS,
} from "./formula-autocomplete";

/**
 * Interface for formula validation service
 */
interface FormulaValidationService {
  validateFormula(formula: string): { isValid: boolean; error?: string };
  getColumnReferences(
    fieldDefs?: FieldDefinition[],
    availableMetrics?: MetricDefinition[],
  ): ColumnReference[];
  getAvailableFunctions(): string[];
  parseFormulaContext(formula: string, cursorPosition: number): FormulaContext;
  getFunctionSignature(functionName: string): FunctionSignature | undefined;
  validateFormulaVariables(
    formula: string,
    columnReferences: ColumnReference[],
  ): { isValid: boolean; unknownVariables?: string[] };
}

/**
 * Dialog component for creating and editing formula columns.
 * Provides autocomplete, validation, and examples for Excel-like formulas.
 */
interface FormulaColumnBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (
    column: Omit<FormulaColumnDefinition, "id" | "createdAt" | "updatedAt">,
  ) => void;
  onDelete?: (id: string) => void;
  validationService: FormulaValidationService;
  existingColumn?: FormulaColumnDefinition;
  fieldDefs?: FieldDefinition[];
  availableMetrics?: MetricDefinition[];
}

const DATA_TYPE_OPTIONS = [
  { value: "number", label: "Number", icon: <Hash className="h-3 w-3" /> },
  { value: "string", label: "Text", icon: <Type className="h-3 w-3" /> },
  {
    value: "currency",
    label: "Currency",
    icon: <DollarSign className="h-3 w-3" />,
  },
  {
    value: "percentage",
    label: "Percentage",
    icon: <Percent className="h-3 w-3" />,
  },
  { value: "date", label: "Date", icon: <Calendar className="h-3 w-3" /> },
] as const;

// Helper component for labels with info popovers
function FieldLabel({
  htmlFor,
  children,
  tooltip,
}: {
  htmlFor: string;
  children: React.ReactNode;
  tooltip: string;
}) {
  return (
    <div className="flex items-center gap-1">
      <Label htmlFor={htmlFor}>{children}</Label>
      <InfoTooltip
        trigger={
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`Info about ${children}`}
          >
            <InfoCircledIcon className="h-3.5 w-3.5" />
          </button>
        }
        side="top"
        className="w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none"
        sideOffset={5}
      >
        <p className="text-sm">{tooltip}</p>
      </InfoTooltip>
    </div>
  );
}

// Example formulas that users can click to populate
const EXAMPLE_FORMULAS = [
  {
    formula: "=METRICS.ACCOUNTS_ENGAGED/METRICS.TOTAL_ENGAGEMENTS*100",
    description: "Engagement Rate (%)",
    dataType: "percentage" as const,
  },
  {
    formula:
      "=IF(METRICS.PEOPLE_ENGAGED>0,METRICS.ACCOUNTS_ENGAGED/METRICS.PEOPLE_ENGAGED,0)",
    description: "Accounts per Person",
    dataType: "number" as const,
  },
  {
    formula:
      "=METRICS.OPPORTUNITIES_INFLUENCED_AMOUNT/METRICS.OPPORTUNITIES_INFLUENCED",
    description: "Average Deal Size",
    dataType: "currency" as const,
  },
  {
    formula:
      "=ROUND((METRICS.ACCOUNTS_ENGAGED*0.3+METRICS.OPPORTUNITIES_INFLUENCED*0.7)/2,1)",
    description: "Campaign Score",
    dataType: "number" as const,
  },
];

export function FormulaColumnBuilder({
  open,
  onOpenChange,
  onSave,
  onDelete,
  validationService,
  existingColumn,
  fieldDefs,
  availableMetrics,
}: FormulaColumnBuilderProps) {
  const [name, setName] = useState("");
  const [formula, setFormula] = useState("");
  const [description, setDescription] = useState("");
  const [dataType, setDataType] =
    useState<FormulaColumnDefinition["dataType"]>("number");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formulaValidation, setFormulaValidation] = useState<{
    isValid: boolean;
    error?: string;
  }>({ isValid: true });

  // Ref for auto-focusing the name input
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Helper function to add equals sign if needed
  function addEqualsSignIfNeeded(formula: string): string {
    if (!formula) {
      // no need to add equals sign for an empty formula
      return formula;
    }
    if (formula.startsWith("=")) {
      return formula;
    }
    return `=${formula}`;
  }

  // Wrapper function that prepends equals sign when needed
  const prependFormula = (formula: string | ((prev: string) => string)) => {
    if (typeof formula === "string") {
      setFormula(addEqualsSignIfNeeded(formula));
    } else {
      setFormula((prev) => addEqualsSignIfNeeded(formula(prev)));
    }
  };

  // Create parser and serializer instances using TypeWire
  const formulaParser = useTypeWire(FormulaParserWire);
  const astSerializer = useTypeWire(ASTSerializerWire);

  // Reset form when dialog opens/closes or existing column changes
  useEffect(() => {
    if (open) {
      if (existingColumn) {
        setName(existingColumn.name);

        // Use original formula if available, otherwise convert from AST
        if (existingColumn.originalFormula) {
          setFormula(existingColumn.originalFormula);
        } else if (
          existingColumn.parseError ||
          existingColumn.isValid === false
        ) {
          // Fallback for old invalid formulas without originalFormula
          setFormula(
            `// Error: ${existingColumn.parseError || "Invalid formula"}`,
          );
        } else {
          // Convert valid AST back to formula string for editing
          setFormula(astSerializer.astToFormula(existingColumn.formulaAST));
        }

        // Set validation state based on column validity
        if (existingColumn.parseError || existingColumn.isValid === false) {
          setFormulaValidation({
            isValid: false,
            error: existingColumn.parseError || "Invalid formula",
          });
        } else {
          setFormulaValidation({ isValid: true });
        }

        setDescription(existingColumn.description || "");
        setDataType(existingColumn.dataType);
      } else {
        setName("");
        setFormula("");
        setDescription("");
        setDataType("number");
        setFormulaValidation({ isValid: true });
      }

      // Auto-focus the name input when dialog opens
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 100);
    }
  }, [open, existingColumn, astSerializer]);

  const columnReferences = useMemo(() => {
    return validationService.getColumnReferences(fieldDefs, availableMetrics);
  }, [validationService, fieldDefs, availableMetrics]);

  const availableFunctions = useMemo(() => {
    return validationService.getAvailableFunctions();
  }, [validationService]);

  // Validate formula in real-time
  useEffect(() => {
    if (formula.trim()) {
      // Check syntax
      const syntaxValidation = validationService.validateFormula(formula);

      if (!syntaxValidation.isValid) {
        setFormulaValidation(syntaxValidation);
        return;
      }

      // Then check for unknown variables
      const variableValidation = validationService.validateFormulaVariables(
        formula,
        columnReferences,
      );

      if (!variableValidation.isValid) {
        setFormulaValidation({
          isValid: false,
          error: `Unknown variables: ${variableValidation.unknownVariables?.join(
            ", ",
          )}`,
        });
        return;
      }

      // All validations passed
      setFormulaValidation({ isValid: true });
    } else {
      setFormulaValidation({ isValid: true });
    }
  }, [formula, columnReferences, validationService]);

  // Ref to hold the insert function from FormulaAutocomplete
  const insertAtCursorRef = useRef<((text: string) => void) | null>(null);

  const setInsertAtCursorFn = useCallback(
    (fn: ((text: string) => void) | null) => {
      insertAtCursorRef.current = fn;
    },
    [],
  );

  const handleColumnReferenceClick = useCallback(
    (reference: ColumnReference) => {
      const columnName = reference?.key?.toUpperCase();

      if (!columnName) {
        return;
      }

      if (insertAtCursorRef.current) {
        insertAtCursorRef.current(columnName);
      } else {
        // Fallback to appending at end
        prependFormula((prev) => prev + columnName);
      }
    },
    [
      // Fallback to appending at end
      prependFormula,
    ],
  );

  const handleFunctionClick = useCallback(
    (func: string) => {
      if (!func) {
        return;
      }

      if (insertAtCursorRef.current) {
        const functionText = `${func}()`;
        insertAtCursorRef.current(functionText);
      } else {
        // Fallback to appending at end
        prependFormula((prev) => `${prev}${func}()`);
      }
    },
    [
      // Fallback to appending at end
      prependFormula,
    ],
  );

  const handleExampleClick = useCallback(
    (example: (typeof EXAMPLE_FORMULAS)[0]) => {
      setFormula(example.formula);
      setDataType(example.dataType);
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!name.trim() || !formula.trim() || !formulaValidation.isValid) {
      return;
    }

    // Parse formula to AST
    const parseResult = formulaParser.parse(formula.trim());

    if (!parseResult.isValid || !parseResult.ast) {
      // Store parsing error in the column definition
      onSave({
        name: name.trim(),
        formulaAST: {
          type: ASTNodeType.LITERAL,
          value: 0,
          dataType: "number",
        } as LiteralNode, // Placeholder AST for invalid formulas
        description: description.trim() || undefined,
        dataType,
        parseError: parseResult.error || "Failed to parse formula",
        isValid: false,
        originalFormula: formula.trim(), // Store original formula for recovery
      });
    } else {
      // Valid formula - save with AST and clear any previous errors
      onSave({
        name: name.trim(),
        formulaAST: parseResult.ast,
        description: description.trim() || undefined,
        dataType,
        parseError: undefined,
        isValid: true,
        originalFormula: formula.trim(), // Store original formula for reference
      });
    }

    onOpenChange(false);
  }, [
    name,
    formula,
    description,
    dataType,
    formulaValidation.isValid,
    formulaParser,
    onSave,
    onOpenChange,
  ]);

  const handleDeleteConfirm = useCallback(() => {
    if (!existingColumn || !onDelete) return;

    setIsDeleting(true);
    try {
      onDelete(existingColumn.id);
      onOpenChange(false);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [existingColumn, onDelete, onOpenChange]);

  const handleDelete = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const isFormValid =
    name.trim() && formula.trim().startsWith("=") && formulaValidation.isValid;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-6 bg-card">
          <DialogHeader className="p-1">
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              {existingColumn ? "Edit Formula Column" : "Create Formula Column"}
            </DialogTitle>
            <DialogDescription>
              Create a formula column using Excel-like formulas to calculate
              values from other columns.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 p-1 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-6">
              {/* Main Form */}
              <div className="lg:col-span-3 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="column-name"
                      tooltip="A descriptive name for your formula column that will appear in the table header"
                    >
                      Column Name <span className="text-destructive">*</span>
                    </FieldLabel>
                    <Input
                      ref={nameInputRef}
                      id="column-name"
                      placeholder="Enter a name for this column (required)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={cn(
                        !name.trim() &&
                          "border-destructive/50 focus-visible:ring-destructive/50",
                      )}
                      aria-required="true"
                    />
                  </div>

                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="data-type"
                      tooltip="How the calculated values should be formatted and displayed in the table"
                    >
                      Data Type
                    </FieldLabel>
                    <Select
                      value={dataType}
                      onValueChange={(value: string) =>
                        setDataType(
                          value as FormulaColumnDefinition["dataType"],
                        )
                      }
                    >
                      <SelectTrigger id="data-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DATA_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              {option.icon}
                              {option.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <FieldLabel
                    htmlFor="description"
                    tooltip="Optional description that will appear in tooltips when users hover over calculated values"
                  >
                    Description (Optional)
                  </FieldLabel>
                  <Textarea
                    id="description"
                    placeholder="Describe what this column calculates..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="min-h-[60px]"
                  />
                </div>

                <div className="space-y-2">
                  <FieldLabel
                    htmlFor="formula"
                    tooltip="Excel-like formula using column names and functions. Must start with '=' (equals sign). Use autocomplete for suggestions as you type."
                  >
                    Formula <span className="text-destructive">*</span>
                  </FieldLabel>
                  {/* Reminder of the formula syntax */}
                  <div className="text-xs text-muted-foreground mt-1 flex items-start gap-2">
                    <span>All formulas must begin with an equals sign (=)</span>
                  </div>
                  <div className="relative">
                    <FormulaAutocomplete
                      value={formula}
                      onChange={prependFormula}
                      columnReferences={columnReferences}
                      availableFunctions={availableFunctions}
                      placeholder="e.g., =METRICS.ACCOUNTS_ENGAGED/METRICS.TOTAL_ENGAGEMENTS*100"
                      onInsertAtCursor={setInsertAtCursorFn}
                      validationService={validationService}
                      className={
                        formula && !formulaValidation.isValid
                          ? "border-destructive focus-visible:ring-destructive"
                          : formula && formulaValidation.isValid
                            ? "border-success focus-visible:ring-success"
                            : ""
                      }
                    />
                    <div className="absolute top-2 right-2 pointer-events-none">
                      {formula &&
                        (formulaValidation.isValid ? (
                          <CheckCircle className="h-4 w-4 text-success" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-destructive" />
                        ))}
                    </div>
                  </div>

                  {/* Display validation error message */}
                  {formula &&
                    !formulaValidation.isValid &&
                    formulaValidation.error && (
                      <div className="text-sm text-destructive mt-1 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{formulaValidation.error}</span>
                      </div>
                    )}
                </div>

                {/* Examples section moved to left column */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Quick Examples</h3>
                  <ScrollArea className="h-32 sm:h-48 border rounded-md p-3">
                    <div className="space-y-2">
                      {EXAMPLE_FORMULAS.map((example, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleExampleClick(example)}
                          className="w-full text-left p-3 rounded-md border border-border hover:bg-accent transition-colors"
                          aria-label={`Example formula: ${example.description} using ${example.formula}`}
                        >
                          <div className="font-mono text-xs text-primary mb-1">
                            {example.formula}
                          </div>
                          <div className="text-sm font-medium">
                            {example.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>

              {/* Reference Panel - Now with more space */}
              <div className="lg:col-span-2 space-y-4 lg:border-l border-border lg:pl-6">
                <div>
                  <div className="flex items-center gap-1 mb-3">
                    <h3 className="text-sm font-medium">Available Values</h3>
                    <InfoTooltip
                      trigger={
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label="Info about Available Values"
                        >
                          <InfoCircledIcon className="h-3.5 w-3.5" />
                        </button>
                      }
                      side="top"
                      className="w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none"
                      sideOffset={5}
                    >
                      <p className="text-sm">
                        Click any value to insert it into your formula. These
                        represent the data available for calculations.
                      </p>
                    </InfoTooltip>
                  </div>
                  <ScrollArea className="h-32 sm:h-48 lg:h-64 border rounded-md p-3">
                    <div className="space-y-2">
                      {columnReferences.map((ref) => (
                        <InfoTooltip
                          key={ref.key}
                          trigger={
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs font-mono h-auto py-2"
                              onClick={() => handleColumnReferenceClick(ref)}
                            >
                              <div className="flex items-center gap-2 w-full">
                                <Badge className="text-xs flex-shrink-0">
                                  {ref.category}
                                </Badge>
                                <span className="truncate">
                                  {ref.key.toUpperCase()}
                                </span>
                              </div>
                            </Button>
                          }
                          side="left"
                          className="max-w-xs"
                        >
                          <div>
                            <p className="font-medium">{ref.displayName}</p>
                            <p className="text-xs mt-1">{ref.description}</p>
                            <p className="text-xs mt-1">Type: {ref.dataType}</p>
                          </div>
                        </InfoTooltip>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                <Separator />

                <div>
                  <div className="flex items-center gap-1 mb-3">
                    <h3 className="text-sm font-medium">Functions</h3>
                    <InfoTooltip
                      trigger={
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label="Info about Functions"
                        >
                          <InfoCircledIcon className="h-3.5 w-3.5" />
                        </button>
                      }
                      side="top"
                      className="w-72 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md outline-none"
                      sideOffset={5}
                    >
                      <p className="text-sm">
                        Click any function to insert it into your formula.
                        Functions perform calculations and operations on your
                        data.
                      </p>
                    </InfoTooltip>
                  </div>
                  <ScrollArea className="h-32 sm:h-40 lg:h-48 border rounded-md p-3">
                    <div className="space-y-1">
                      {availableFunctions
                        .sort((a, b) => a.localeCompare(b))
                        .map((func) => (
                          <InfoTooltip
                            key={func}
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full text-xs font-mono justify-start h-auto py-2"
                                onClick={() => handleFunctionClick(func)}
                              >
                                {func}()
                              </Button>
                            }
                            side="left"
                            className="max-w-xs"
                          >
                            <div>
                              <p className="font-medium">{func}()</p>
                              <p className="text-xs mt-1">
                                {FUNCTION_DESCRIPTIONS[func] ||
                                  `${func} function`}
                              </p>
                            </div>
                          </InfoTooltip>
                        ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>

            {existingColumn && onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Delete Formula Column
              </Button>
            )}

            <Button type="button" onClick={handleSave} disabled={!isFormValid}>
              {existingColumn ? "Update Column" : "Create Column"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Formula Column</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the formula column "
              {existingColumn?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
